import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Sparkles, Plus, Send, Trash2, Pencil, Shield, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const MODE_LABEL = {
  research_question: "Research Question Coach",
  methodology: "Methodology Tutor",
  irb: "IRB Preparation",
  literature_review: "Literature Review",
  data_analysis: "Data Analysis Guide",
  academic_writing: "Academic Writing Feedback",
  defense_prep: "Defense Preparation",
  general: "General Companion",
};

export default function AICompanion() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [thread, setThread] = useState(null);
  const [modes, setModes] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [includeProfile, setIncludeProfile] = useState(false);
  const [includeStage, setIncludeStage] = useState(false);
  const [contextText, setContextText] = useState("");
  const [showContext, setShowContext] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQ.trim()) params.set("q", searchQ.trim());
      const [c, m] = await Promise.all([
        api.get(`/ai/conversations${params.toString() ? "?" + params.toString() : ""}`),
        api.get("/ai/starters"),
      ]);
      setConvs(c.data.results);
      setModes(m.data.modes);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [searchQ]);

  useEffect(() => { load(); }, [load]);

  const openConv = async (cid) => {
    setOpenId(cid);
    try {
      const { data } = await api.get(`/ai/conversations/${cid}`);
      setThread(data);
      setRenameVal(data.title);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startFromMode = async (mode) => {
    try {
      const { data } = await api.post("/ai/conversations", { mode });
      await load();
      await openConv(data.id);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!input.trim() || !openId) return;
    setBusy(true);
    try {
      const payload = { content: input.trim() };
      if (includeProfile) payload.include_profile = true;
      if (includeStage) payload.include_stage = true;
      if (contextText.trim()) payload.context_text = contextText.trim();
      const { data } = await api.post(`/ai/conversations/${openId}/messages`, payload);
      setThread(t => ({ ...t, messages: [...(t?.messages || []), data.user_message, data.assistant_message] }));
      setInput(""); setContextText(""); setShowContext(false);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this conversation? This cannot be undone.")) return;
    try {
      await api.delete(`/ai/conversations/${cid}`);
      toast.success("Conversation deleted");
      if (openId === cid) { setOpenId(null); setThread(null); }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rename = async () => {
    if (!renameVal.trim() || !openId) return;
    try {
      await api.patch(`/ai/conversations/${openId}`, { title: renameVal.trim() });
      setRenaming(false);
      await load();
      await openConv(openId);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!user) return null;

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="ai-page">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <Sparkles className="h-6 w-6 text-primary" />
          <h1 className="font-serif text-3xl font-black tracking-tight">DocCircle AI</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground max-w-xl">
          Your private doctoral research companion. A methodology tutor and scholarly thinking partner —
          not a dissertation ghostwriter.
        </p>
        <div className="mt-3 inline-flex items-start gap-2 text-[11px] text-muted-foreground bg-muted/50 border border-border rounded-md px-3 py-1.5 max-w-3xl" data-testid="ai-privacy-notice">
          <Shield className="h-3 w-3 mt-0.5 shrink-0" />
          <span>
            Your DocCircle AI conversations are private to your account and are not shared with
            Circles, mentors, or other members. DocCircle is currently running a local research
            companion — your messages are not sent to any external AI provider. When external AI
            processing is enabled in the future, information you submit to AI features may be
            transmitted to our AI provider for processing under their API privacy terms; we will
            update this notice at that time.
          </span>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Sidebar */}
        <Card className="lg:col-span-1 p-0 overflow-hidden" data-testid="ai-sidebar">
          <div className="p-3 border-b space-y-2">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Conversations</p>
            <Input data-testid="ai-search-input" className="h-8 text-sm"
              placeholder="Search your conversations…"
              value={searchQ} onChange={e => setSearchQ(e.target.value)} />
          </div>
          {convs.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="ai-empty">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-60" />
              {searchQ.trim() ? "No conversations found." : (<>No conversations yet.<br/>Pick a starter to begin.</>)}
              {searchQ.trim() && (
                <button data-testid="ai-search-clear"
                  onClick={() => setSearchQ("")}
                  className="mt-3 text-xs underline text-primary">Clear search</button>
              )}
            </div>
          ) : (
            <ul className="divide-y max-h-[480px] overflow-y-auto">
              {convs.map(c => (
                <li key={c.id} className={`flex items-center ${openId === c.id ? "bg-muted/60" : ""}`}>
                  <button data-testid={`ai-conv-${c.id}`} onClick={() => openConv(c.id)}
                    className="flex-1 text-left px-3 py-2 hover:bg-muted/40 transition-colors min-w-0">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {MODE_LABEL[c.mode]} · {new Date(c.updated_at).toLocaleDateString()}
                    </p>
                  </button>
                  <button data-testid={`ai-delete-${c.id}`} onClick={() => remove(c.id)}
                    className="p-2 text-muted-foreground hover:text-red-600" title="Delete">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Chat area */}
        <div className="lg:col-span-3 space-y-4">
          {!openId ? (
            <Card className="p-6" data-testid="ai-starters">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Pick a starter</p>
              <div className="grid md:grid-cols-2 gap-3">
                {modes.map(m => (
                  <button key={m.key} data-testid={`starter-${m.key}`}
                    onClick={() => startFromMode(m.key)}
                    className="text-left p-4 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-2">
                      <Plus className="h-4 w-4 text-primary" />
                      <p className="font-serif text-base font-black">{m.label}</p>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{m.starter}</p>
                  </button>
                ))}
              </div>
            </Card>
          ) : (
            <>
              <Card className="p-4 flex items-center justify-between gap-2" data-testid="ai-header">
                {renaming ? (
                  <div className="flex gap-2 flex-1">
                    <Input data-testid="ai-rename-input" value={renameVal}
                      onChange={e => setRenameVal(e.target.value)} className="h-9" />
                    <Button size="sm" data-testid="ai-rename-save" onClick={rename}>Save</Button>
                    <Button size="sm" variant="outline" onClick={() => setRenaming(false)}>Cancel</Button>
                  </div>
                ) : (
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-serif text-lg font-black truncate">{thread?.title}</p>
                      <button data-testid="ai-rename-btn" className="text-muted-foreground hover:text-foreground"
                        onClick={() => setRenaming(true)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
                      {MODE_LABEL[thread?.mode]}
                    </p>
                  </div>
                )}
              </Card>

              <Card className="p-5 space-y-4 max-h-[480px] overflow-y-auto" data-testid="ai-thread">
                {(thread?.messages || []).length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-6">
                    Send your first message to begin.
                  </p>
                ) : (
                  (thread?.messages || []).map(m => (
                    <div key={m.id} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className={`max-w-[85%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap ${
                        m.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"
                      }`} data-testid={`ai-message-${m.role}`}>
                        {m.content}
                      </div>
                    </div>
                  ))
                )}
                <div ref={bottomRef} />
              </Card>

              {showContext && (
                <Card className="p-4" data-testid="ai-context-panel">
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">Explicit context</p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" data-testid="ai-include-profile" checked={includeProfile}
                        onChange={e => setIncludeProfile(e.target.checked)} />
                      Use my doctoral profile (degree, discipline, methodology)
                    </label>
                    <label className="inline-flex items-center gap-2">
                      <input type="checkbox" data-testid="ai-include-stage" checked={includeStage}
                        onChange={e => setIncludeStage(e.target.checked)} />
                      Use my current Journey stage
                    </label>
                  </div>
                  <Textarea data-testid="ai-context-text" className="mt-3" rows={3} maxLength={6000}
                    placeholder="Paste text you want to explicitly include as context…"
                    value={contextText} onChange={e => setContextText(e.target.value)} />
                  <p className="mt-2 text-[11px] text-muted-foreground" data-testid="ai-context-reminder">
                    Only share research material or other content you are authorized to provide.
                  </p>
                </Card>
              )}

              <div className="flex gap-2 items-end">
                <Textarea data-testid="ai-input" className="flex-1" rows={2} maxLength={8000}
                  placeholder="Ask a scholarly question, paste a paragraph, or explore a methodology…"
                  value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }} />
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="outline" data-testid="ai-context-toggle"
                    onClick={() => setShowContext(v => !v)}>
                    {showContext ? "Hide context" : "Add context"}
                  </Button>
                  <Button size="sm" data-testid="ai-send" onClick={send} disabled={busy || !input.trim()}>
                    <Send className="h-4 w-4 mr-1" /> Send
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
